import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { TimePicker } from '@mui/x-date-pickers-pro';
import { TimeFormat } from '@treadinc/horizon-api-spec';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';
import { FormFieldLabel } from '~components/FormFields/FormFieldLabel';
import {
  fromWallClock,
  SITE_TIME_FORMAT_BY_TIME_FORMAT,
  toWallClock,
} from '~pages/Settings/Administration/Sites/site-form-v2/siteTime';

interface WallClockTimeInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  dataUi?: string;
  disabled?: boolean;
  errors?: FieldErrors<TFieldValues>;
  isRequired?: boolean;
  label: string;
  name: FieldPath<TFieldValues>;
  note?: string;
  timeFormat: TimeFormat;
  timeZoneAbbreviation?: string;
}

export function WallClockTimeInput<TFieldValues extends FieldValues>({
  control,
  dataUi,
  disabled = false,
  errors,
  isRequired = false,
  label,
  name,
  note,
  timeFormat,
  timeZoneAbbreviation,
}: WallClockTimeInputProps<TFieldValues>) {
  const errorMessage = get(errors, `${name}.message`);
  // Replaces the button that opens the picker, so the zone reads as plain text and the
  // time is typed rather than picked.
  const TimeZoneAbbreviationText = useMemo(
    () =>
      timeZoneAbbreviation
        ? function TimeZoneAbbreviationText() {
            return (
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {timeZoneAbbreviation}
              </Typography>
            );
          }
        : null,
    [timeZoneAbbreviation],
  );

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onBlur, onChange, value } }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <FormFieldLabel label={`${label}${isRequired ? ' *' : ''}`} />
          <TimePicker
            ampm={timeFormat === TimeFormat['12_HOUR']}
            disabled={disabled}
            enableAccessibleFieldDOMStructure={false}
            format={SITE_TIME_FORMAT_BY_TIME_FORMAT[timeFormat]}
            onChange={(time) => onChange(toWallClock(time))}
            value={fromWallClock(value)}
            {...(TimeZoneAbbreviationText && {
              slots: { openPickerButton: TimeZoneAbbreviationText },
            })}
            slotProps={{
              actionBar: desktopActionBar(['accept']),
              field: { clearable: true },
              textField: {
                error: Boolean(errorMessage),
                fullWidth: true,
                helperText:
                  typeof errorMessage === 'string' ? errorMessage : (note ?? ''),
                onBlur,
                size: 'small',
                slotProps: { htmlInput: { ...(dataUi && { 'data-ui': dataUi }) } },
              },
            }}
          />
        </Box>
      )}
    />
  );
}
